/**
 * Stable @id identifiers and schema helpers for sitewide structured data.
 *
 * The clinic (MedicalClinic + MedicalBusiness) and WebSite blocks are injected
 * once at the app root by <SitewideSchema> inside a single @graph. Page-level
 * schema references the clinic entity by @id instead of duplicating NAP data.
 */

/** Primary production domain. */
export const SITE_URL = "https://neurowellnesstms.com";

/** @id of the sitewide clinic entity (MedicalClinic + MedicalBusiness). */
export const ORG_ID = `${SITE_URL}/#organization`;

/** @id of the sitewide WebSite entity. */
export const SITE_ID = `${SITE_URL}/#website`;

/** @id of the homepage MedicalWebPage entity. */
export const WEBPAGE_ID = `${SITE_URL}/#webpage`;

/** Permanent CDN URL for the clinic logo (used in schema). */
export const CLINIC_LOGO_URL =
  "https://assets.cdn.filesafe.space/k94aHcRKI8Yx46SYfgfo/media/6a888227b647fb6eca258f39.png";

/** Default OG / schema image on the permanent CDN. */
export const DEFAULT_OG_IMAGE = CLINIC_LOGO_URL;

/**
 * Drop-in reference to the clinic entity. Use this as the value of `provider`,
 * `publisher`, `organizer`, etc. in page-level JSON-LD so the entity is
 * resolved against the single sitewide definition in the @graph.
 *
 *   publisher: CLINIC_REF   // → { "@id": "…/#organization" }
 */
export const CLINIC_REF: { "@id": string } = { "@id": ORG_ID };

/** Drop-in reference to the WebSite entity. */
export const WEBSITE_REF: { "@id": string } = { "@id": SITE_ID };

export interface Crumb {
  name: string;
  href: string;
}

/**
 * Build a BreadcrumbList JSON-LD object from a visible breadcrumb trail.
 * The crumbs array should include every level from Home to the current page.
 * Each entry must match a visible link on the page (or the canonical URL for
 * the current page).
 */
export function buildBreadcrumbList(crumbs: Crumb[], id?: string): object {
  const base = SITE_URL.replace(/\/$/, "");
  const itemListElement = crumbs.map((c, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: c.name,
    item: `${base}${c.href.startsWith("/") ? c.href : `/${c.href}`}`,
  }));
  const out: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement,
  };
  if (id) out["@id"] = id;
  return out;
}

/**
 * Build an Article JSON-LD object for blog/educational article pages.
 * All fields are sourced from visible page content (title, description,
 * canonical URL, OG image) and the E-E-A-T review metadata (lastUpdated).
 */
export function buildArticleSchema(params: {
  headline: string;
  description: string;
  url: string;
  image: string;
  datePublished?: string;
  dateModified?: string;
}): object {
  const article: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: params.headline,
    description: params.description,
    url: params.url,
    image: {
      "@type": "ImageObject",
      url: params.image,
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": params.url,
    },
    isPartOf: { "@id": SITE_ID },
    inLanguage: "en-US",
    author: {
      "@type": "Organization",
      name: "Neuro Wellness TMS Centers of America",
      url: SITE_URL,
    },
    publisher: {
      "@type": "Organization",
      name: "Neuro Wellness TMS Centers of America",
      logo: {
        "@type": "ImageObject",
        url: CLINIC_LOGO_URL,
      },
      "@id": ORG_ID,
    },
  };
  if (params.datePublished) {
    article.datePublished = params.datePublished;
  }
  if (params.dateModified) {
    article.dateModified = params.dateModified;
  }
  return article;
}

/**
 * Build a MedicalWebPage JSON-LD object for a clinical/treatment page.
 * Links to the sitewide WebSite + clinic entities by @id so Google resolves
 * the entity once from the sitewide @graph instead of re-declaring NAP data.
 */
export function buildMedicalWebPage(params: {
  name: string;
  description: string;
  url: string;
  id?: string;
  about?: { "@id": string };
  breadcrumbId?: string;
}): object {
  const page: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "MedicalWebPage",
    name: params.name,
    description: params.description,
    url: params.url,
    inLanguage: "en-US",
    isPartOf: { "@id": SITE_ID },
    provider: { "@id": ORG_ID },
  };
  if (params.id) page["@id"] = params.id;
  if (params.about) page.about = params.about;
  if (params.breadcrumbId) page.breadcrumb = { "@id": params.breadcrumbId };
  return page;
}

/**
 * Build a MedicalProcedure JSON-LD object. Only name, description, and url
 * are included — all sourced from visible page content (title/description).
 */
export function buildMedicalProcedure(
  name: string,
  description: string,
  url: string,
): object {
  return {
    "@context": "https://schema.org",
    "@type": "MedicalProcedure",
    name,
    description,
    url,
  };
}

/**
 * Build a MedicalCondition JSON-LD object. Only name, description, and url
 * are included — all sourced from visible page content (title/description).
 */
export function buildMedicalCondition(
  name: string,
  description: string,
  url: string,
): object {
  return {
    "@context": "https://schema.org",
    "@type": "MedicalCondition",
    name,
    description,
    url,
  };
}

/**
 * MedicalClinic + LocalBusiness block carrying the Coral Springs NAP address.
 *
 * Uses the same @id as the sitewide clinic entity (ORG_ID) so Google merges it
 * with the root @graph definition rather than treating it as a duplicate.
 * Page-level JSON-LD may repeat the address; the stable @id keeps it one entity.
 */
export function buildMedicalClinicLocal(): object {
  return {
    "@context": "https://schema.org",
    "@type": ["MedicalClinic", "LocalBusiness"],
    "@id": ORG_ID,
    name: "Neuro Wellness TMS Centers of America",
    image: CLINIC_LOGO_URL,
    url: SITE_URL,
    telephone: "+19549031323",
    priceRange: "$$",
    address: {
      "@type": "PostalAddress",
      streetAddress: "9734 West Sample Road, 2nd Floor, Suite B",
      addressLocality: "Coral Springs",
      addressRegion: "FL",
      postalCode: "33065",
      addressCountry: "US",
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: 26.2714,
      longitude: -80.2706,
    },
    openingHoursSpecification: [
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        opens: "07:00",
        closes: "19:00",
      },
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: "Saturday",
        opens: "09:00",
        closes: "13:00",
      },
    ],
  };
}
